import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDuration, statusPillClasses, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Trash2, AlertTriangle, Lock, Paperclip, Upload, FileText, Download, ChevronUp } from "lucide-react";

import StatusInline from "@/components/inline/StatusInline";
import AssigneeInline from "@/components/inline/AssigneeInline";
import PriorityInline from "@/components/inline/PriorityInline";
import DueDateInline from "@/components/inline/DueDateInline";
import ActivityFeed from "@/components/task/ActivityFeed";
import SubtasksSection from "@/components/task/SubtasksSection";
import DependenciesSection from "@/components/task/DependenciesSection";
import TimeBreakdown from "@/components/task/TimeBreakdown";

function humanSize(bytes) {
  if (!bytes) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let n = bytes; let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function TaskSheet({ open, taskId, list, users, allTasks, canEdit, onOpenChange, onChange, onDelete, onOpenTask }) {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [parent, setParent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("orbit_token") : "";

  const refresh = useCallback(async () => {
    if (!taskId) return;
    try {
      const [{ data: t }, { data: a }] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/attachments`),
      ]);
      setTask(t.task);
      setAttachments(a.attachments);
      if (t.task.parent_task_id) {
        const p = await api.get(`/tasks/${t.task.parent_task_id}`);
        setParent(p.data.task);
      } else {
        setParent(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load task");
      onOpenChange(false);
    }
  }, [taskId, onOpenChange]);

  useEffect(() => { if (open && taskId) refresh(); }, [open, taskId, refresh]);

  const update = async (patch) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, patch);
      setTask(data.task);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    const count = list.length;
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/tasks/${task.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${count} file${count > 1 ? "s" : ""}`);
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const deleteAttachment = async (id) => {
    try { await api.delete(`/attachments/${id}`); await refresh(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!task) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0">
          <SheetHeader className="sr-only"><SheetTitle>Loading</SheetTitle></SheetHeader>
          <div className="p-6 font-mono-data text-xs uppercase tracking-widest text-neutral-500">Loading…</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 overflow-y-auto" data-testid="task-sheet">
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-neutral-200 bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <StatusInline list={list} task={task} canEdit={canEdit} onChange={(v) => update({ status_id: v })} />
            <span className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">{list.name}</span>
            {parent && (
              <button
                onClick={() => onOpenTask(parent.id)}
                className="ml-auto flex items-center gap-1 font-mono-data text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 transition-colors"
                data-testid="task-parent-link"
              >
                <ChevronUp className="w-3 h-3" />Parent: {parent.title}
              </button>
            )}
          </div>
          {canEdit ? (
            <Input
              defaultValue={task.title}
              onBlur={(e) => { if (e.target.value !== task.title) update({ title: e.target.value }); }}
              className="text-xl font-bold rounded-sm h-12 border-neutral-200 font-display"
              data-testid="task-title-input"
            />
          ) : (
            <SheetTitle className="font-display text-xl tracking-tight">{task.title}</SheetTitle>
          )}
        </SheetHeader>

        <div className="p-6 space-y-6">
          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 bg-neutral-50 border border-neutral-200 rounded-sm p-4">
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Assignees</Label>
              <div className="mt-1.5"><AssigneeInline task={task} users={users} canEdit={canEdit} onChange={(v) => update({ assignee_ids: v })} /></div>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Priority</Label>
              <div className="mt-1.5"><PriorityInline task={task} canEdit={canEdit} onChange={(v) => update({ priority: v })} /></div>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Start date</Label>
              <div className="mt-1.5"><DueDateInline task={task} field="start_date" label="Start" canEdit={canEdit} onChange={(v) => update({ start_date: v })} /></div>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Due date</Label>
              <div className="mt-1.5"><DueDateInline task={task} field="due_date" label="Due" canEdit={canEdit} onChange={(v) => update({ due_date: v })} /></div>
            </div>
            <div className="col-span-2">
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Time tracked</Label>
              <div className="mt-1.5"><TimeBreakdown task={task} users={users} /></div>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Description</Label>
            <Textarea
              defaultValue={task.description || ""}
              disabled={!canEdit}
              onBlur={(e) => { if (e.target.value !== (task.description || "")) update({ description: e.target.value }); }}
              placeholder="Add context, acceptance criteria…"
              className="mt-1.5 rounded-sm border-neutral-200 min-h-[100px]"
              data-testid="task-description-input"
            />
          </div>

          {/* Subtasks */}
          <SubtasksSection task={task} list={list} users={users} canEdit={canEdit} onOpenTask={onOpenTask} onChange={onChange} />

          {/* Dependencies */}
          <DependenciesSection task={task} list={list} allTasks={allTasks} canEdit={canEdit} onOpenTask={onOpenTask} onChange={onChange} />

          {/* Attachments (task-level) */}
          <section data-testid="attachments-section">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachments ({attachments.length})
              </Label>
              {canEdit && (
                <Button onClick={() => fileRef.current?.click()} variant="outline" disabled={uploading} className="h-7 rounded-sm text-xs" data-testid="upload-attachment-button">
                  <Upload className="w-3 h-3 mr-1" />{uploading ? "Uploading…" : "Upload"}
                </Button>
              )}
            </div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="attachment-file-input" />
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (canEdit) handleFiles(e.dataTransfer.files); }}
              className="border border-dashed border-neutral-200 rounded-sm"
              data-testid="attachments-list"
            >
              {attachments.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-neutral-500">
                  {canEdit ? "Drop files here or click Upload" : "No attachments"}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-2" data-testid="attachments-grid">
                  {attachments.map((a) => {
                    const isImg = (a.content_type || "").startsWith("image/");
                    const url = `${API_BASE}/attachments/${a.id}/download?auth=${encodeURIComponent(token)}`;
                    return (
                      <div key={a.id} className="bg-white border border-neutral-200 rounded-sm overflow-hidden group" data-testid={`attachment-${a.id}`}>
                        {isImg ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" className="block aspect-video bg-neutral-50">
                            <img src={url} alt={a.filename} className="w-full h-full object-cover" />
                          </a>
                        ) : (
                          <div className="aspect-video bg-neutral-50 flex items-center justify-center">
                            <FileText className="w-7 h-7 text-neutral-400" />
                          </div>
                        )}
                        <div className="p-2">
                          <div className="text-xs font-medium truncate" title={a.filename}>{a.filename}</div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="font-mono-data text-[10px] text-neutral-500">{humanSize(a.size)}</span>
                            <div className="flex items-center gap-1">
                              <a href={url} download={a.filename} className="text-neutral-400 hover:text-neutral-900 transition-colors" data-testid={`download-attachment-${a.id}`}>
                                <Download className="w-3.5 h-3.5" />
                              </a>
                              {canEdit && (
                                <button onClick={() => deleteAttachment(a.id)} className="text-neutral-400 hover:text-red-600 transition-colors" data-testid={`delete-attachment-${a.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Activity + comments */}
          <ActivityFeed task={task} list={list} users={users} currentUserId={user?.id} canEdit={canEdit} onChange={onChange} />

          <div className="pt-4 border-t border-neutral-200 flex items-center justify-between font-mono-data text-[10px] text-neutral-500">
            <div>CREATED · {formatDate(task.created_at)} · UPDATED · {formatDate(task.updated_at)} · ID · {task.id.slice(0, 8)}</div>
            {canEdit && (
              <button onClick={() => { onDelete(task.id); onOpenChange(false); }} className="text-red-600 hover:underline flex items-center gap-1" data-testid="task-delete-button">
                <Trash2 className="w-3 h-3" /> Delete task
              </button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
